import { finances, player, realRosters, season, team } from "../index.ts";
import { idb } from "../../db/index.ts";
import { achievement, g, helpers, local, logEvent } from "../../util/index.ts";
import type {
	Conditions,
	PhaseReturn,
	PlayoffSeries,
} from "../../../common/types.ts";
import { PHASE } from "../../../common/constants.ts";

const newPhasePlayoffs = async (
	conditions: Conditions,
	liveGameSim: boolean = false,
): Promise<PhaseReturn> => {
	await achievement.check("afterRegularSeason", conditions);

	// In case this was somehow set already
	local.playingUntilEndOfRound = false;

	// Set playoff matchups
	const { byConf, playIns, series, tidPlayIn, tidPlayoffs } =
		await season.genPlayoffSeries();

	for (const type of ["playoffs", "play-in tournament"] as const) {
		const tids = type === "playoffs" ? tidPlayoffs : tidPlayIn;

		for (const tid of tids) {
			logEvent(
				{
					type: "madePlayoffs",
					text: `The <a href="${helpers.leagueUrl([
						"roster",
						`${g.get("teamInfoCache")[tid]?.abbrev}_${tid}`,
						g.get("season"),
					])}">${
						g.get("teamInfoCache")[tid]?.name
					}</a> made the <a href="${helpers.leagueUrl([
						"playoffs",
						g.get("season"),
					])}">${type}</a>.`,
					showNotification: tid === g.get("userTid"),
					tids: [tid],
					score: 0,
				},
				conditions,
			);
		}
	}

	const playoffSeries: PlayoffSeries = {
		byConf,
		season: g.get("season"),
		currentRound: 0,
		series,
	};
	if (playIns) {
		playoffSeries.currentRound = -1;
		playoffSeries.playIns = playIns;
	}
	await idb.cache.playoffSeries.put(playoffSeries);

	// Add row to team stats and team season attributes
	const teamSeasons = await idb.cache.teamSeasons.indexGetAll(
		"teamSeasonsBySeasonTid",
		[[g.get("season")], [g.get("season"), "Z"]],
	);

	const tidAll = new Set([...tidPlayoffs, ...tidPlayIn]);

	const teamStatsToSave = [];
	for (const teamSeason of teamSeasons) {
		if (tidAll.has(teamSeason.tid)) {
			teamStatsToSave.push(team.genStatsRow(teamSeason.tid, true));

			// Play-in teams have not made the playoffs yet, technically
			if (tidPlayoffs.includes(teamSeason.tid)) {
				teamSeason.playoffRoundsWon = 0;
			}

			// More hype for making the playoffs
			teamSeason.hype += 0.05;
		} else {
			// Less hype for missing the playoffs
			teamSeason.hype -= 0.05;
		}
		teamSeason.hype = helpers.bound(teamSeason.hype, 0, 1);

		// Average age and team ovr, cache now that season is over
		const playersRaw = await idb.cache.players.indexGetAll(
			"playersByTid",
			teamSeason.tid,
		);
		const players = await player.formatAll(playersRaw, {
			attrs: ["age", "injury", "value", "pid"],
			fuzz: true,
			stats: ["gp", "min"],
			ratings: ["ovr", "pos", "ovrs"],
			season: g.get("season"),
			tid: teamSeason.tid,
		});
		teamSeason.avgAge = team.avgAge(players);
		teamSeason.ovrEnd = team.ovr(players);
	}
	await idb.cache.teamStats.addAll(teamStatsToSave);
	await idb.cache.teamSeasons.putAll(teamSeasons);

	await finances.assessPayrollMinLuxury();
	await season.newSchedulePlayoffsDay();

	// Update clinchedPlayoffs with final values
	await team.updateClinchedPlayoffs(true, conditions);

	await realRosters.checkDisableForceHistoricalRosters(
		g.get("season"),
		PHASE.PLAYOFFS,
	);

	// Don't redirect if we're viewing a live game now
	let redirect;
	if (!liveGameSim) {
		redirect = {
			url: helpers.leagueUrl(["playoffs"]),
			text: "View playoff bracket",
		};
	}

	return {
		redirect,
		updateEvents: ["teamFinances"],
	};
};

export default newPhasePlayoffs;
